import { z } from "zod";

/**
 * The RCS lane of `message_contents.rendered_output`, holding compiled Liquid
 * templates for a rich card. Sibling of the SMS lane's `body` — the two are
 * authored on separate tabs and neither derives from the other.
 *
 * Provider-neutral: this is what the author wrote, not how any provider takes
 * it. How the card becomes a Twilio Content Template, and which fields turn
 * into `{{key}}` holes, lives in
 * `messaging/providers/sms/twilio-content-variables.ts`.
 */

/** RBM media `height`: how tall the media renders on Android. */
export const RCS_MEDIA_HEIGHTS = ["SHORT", "MEDIUM", "TALL"] as const;
export type RcsMediaHeight = (typeof RCS_MEDIA_HEIGHTS)[number];

/** A card action. Provider mapping (`URL`, `PHONE_NUMBER`) happens at compile. */
export const RCS_BUTTON_TYPES = ["url", "phone"] as const;
export type RcsButtonType = (typeof RCS_BUTTON_TYPES)[number];

/**
 * RCS caps a rich card at four actions. Twilio accepts more and spills the
 * remainder into a chip list, which silently changes the rendering — so the
 * cap is enforced here rather than left to the provider.
 */
export const RCS_MAX_BUTTONS = 4;

/**
 * How many `phone` buttons a card may carry. Provider-imposed rather than an RCS
 * rendering limit, so the rule itself is stated on
 * `TWILIO_CARD_MAX_CALL_ACTIONS`; the number lives here for the editor to share.
 */
export const RCS_MAX_CALL_BUTTONS = 1;

export const RCS_TITLE_MAX = 200;
export const RCS_DESCRIPTION_MAX = 1600;
export const RCS_BUTTON_LABEL_MAX = 25;

export type RcsMedia = {
  /**
   * A literal URL, or one whose path varies per recipient. A URL whose *domain*
   * varies can't be represented by a frozen provider template, so the publish
   * compiler rejects it rather than letting the send fail.
   */
  url: string;
  height: RcsMediaHeight;
};

export type RcsButton = {
  id: string;
  /** Liquid — RCS permits variables in URL and QUICK_REPLY action titles. */
  label: string;
  type: RcsButtonType;
  /** Literal URL or E.164 phone. Tracked links are rewritten at compile. */
  value: string;
  /**
   * Whether this button's URL is shortened for click attribution. Recorded on
   * the content, not read from channel config, so a later settings change can't
   * invalidate a template that was already compiled and frozen. The editor
   * seeds it from the workspace's link-tracking config at authoring time.
   */
  tracked?: boolean;
};

export type RcsContent = {
  /** Liquid. */
  title: string;
  /** Liquid. */
  body: string;
  media: RcsMedia | null;
  buttons: RcsButton[];
};

const rcsMediaSchema = z.object({
  url: z.string(),
  height: z.enum(RCS_MEDIA_HEIGHTS),
});

const rcsButtonSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  type: z.enum(RCS_BUTTON_TYPES),
  value: z.string(),
  // Tolerant read with a conservative default: an absent flag shouldn't fail
  // the parse. Absent means an untracked, literal URL.
  tracked: z.boolean().default(false),
});

const rcsContentSchema = z.object({
  title: z.string(),
  body: z.string(),
  media: rcsMediaSchema.nullable().default(null),
  buttons: z.array(rcsButtonSchema).max(RCS_MAX_BUTTONS).default([]),
});

/**
 * Reads the RCS lane off a `rendered_output` value, returning null for both a
 * missing lane and one that won't parse.
 *
 */
export function parseRcsContent(renderedOutput: unknown): RcsContent | null {
  if (
    renderedOutput === null ||
    typeof renderedOutput !== "object" ||
    Array.isArray(renderedOutput)
  ) {
    return null;
  }
  const rcs = (renderedOutput as Record<string, unknown>).rcs;
  if (rcs === undefined || rcs === null) return null;
  const parsed = rcsContentSchema.safeParse(rcs);
  return parsed.success ? (parsed.data as RcsContent) : null;
}

/** True when `rendered_output` carries an RCS lane at all, malformed or not. */
export function hasRcsLane(renderedOutput: unknown): boolean {
  if (
    renderedOutput === null ||
    typeof renderedOutput !== "object" ||
    Array.isArray(renderedOutput)
  ) {
    return false;
  }
  const rcs = (renderedOutput as Record<string, unknown>).rcs;
  return rcs !== undefined && rcs !== null;
}

/**
 * Fails loudly where a dropped card would send the wrong message: the publish
 * compiler and the dispatcher both call this, so a malformed lane aborts
 * rather than degrading to a text-only send the author never approved.
 */
export function parseRcsContentOrThrow(renderedOutput: unknown): RcsContent {
  const parsed = parseRcsContent(renderedOutput);
  if (parsed === null) {
    throw new Error(
      "rendered_output.rcs is present but malformed; refusing to compile or send",
    );
  }
  return parsed;
}
